/* ==========================================================================
   PMX — Revenue Infrastructure landing page
   Reveal-on-scroll, FAQ accordions, ROI calculator, counters, sticky CTA.
   Guarded so duplicate <script> tags across sections only initialise once.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__pmxLandingInit) return;
  window.__pmxLandingInit = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Reveal on scroll ---------- */
  function initReveal(root) {
    var items = (root || document).querySelectorAll('.pmx-reveal:not([data-pmx-revealed])');
    if (!items.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.setAttribute('data-pmx-revealed', '1');
        el.classList.add('is-in');
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.getAttribute('data-pmx-delay') || '0', 10);
        setTimeout(function () { el.classList.add('is-in'); }, delay);
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach(function (el) {
      el.setAttribute('data-pmx-revealed', '1');
      io.observe(el);
    });

    /* Failsafe: if the observer never fires (odd viewport, print, bot render),
       force everything visible rather than leaving the page blank. */
    setTimeout(function () {
      items.forEach(function (el) { el.classList.add('is-in'); });
    }, 3000);
  }

  /* ---------- FAQ / accordion ---------- */
  function initAccordions(root) {
    var buttons = (root || document).querySelectorAll('[data-pmx-faq-q]:not([data-pmx-bound])');
    buttons.forEach(function (btn) {
      btn.setAttribute('data-pmx-bound', '1');
      btn.addEventListener('click', function () {
        var item = btn.closest('.pmx-faq__item');
        if (!item) return;
        var open = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  /* ---------- Number counters ---------- */
  function initCounters(root) {
    var nodes = (root || document).querySelectorAll('[data-pmx-count]:not([data-pmx-bound])');
    if (!nodes.length) return;

    function run(el) {
      var target = parseFloat(el.getAttribute('data-pmx-count'));
      if (isNaN(target)) return;
      var prefix = el.getAttribute('data-pmx-prefix') || '';
      var suffix = el.getAttribute('data-pmx-suffix') || '';
      var decimals = parseInt(el.getAttribute('data-pmx-decimals') || '0', 10);
      if (reduceMotion) {
        el.textContent = prefix + target.toFixed(decimals) + suffix;
        return;
      }
      var duration = 1100;
      var start = null;
      function frame(ts) {
        if (start === null) start = ts;
        var p = Math.min((ts - start) / duration, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        var value = target * eased;
        el.textContent = prefix + value.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + suffix;
        if (p < 1) requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    }

    if (!('IntersectionObserver' in window)) {
      nodes.forEach(function (el) { el.setAttribute('data-pmx-bound', '1'); run(el); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        run(entry.target);
        io.unobserve(entry.target);
      });
    }, { threshold: 0.4 });
    nodes.forEach(function (el) { el.setAttribute('data-pmx-bound', '1'); io.observe(el); });
  }

  /* ---------- ROI calculator ---------- */
  function fmtEur(n) {
    return '€' + Math.round(n).toLocaleString('en-US').replace(/,/g, ' ');
  }

  function initRoi(root) {
    var panels = (root || document).querySelectorAll('[data-pmx-roi]:not([data-pmx-bound])');
    panels.forEach(function (panel) {
      panel.setAttribute('data-pmx-bound', '1');

      var traffic = panel.querySelector('[data-pmx-roi-traffic]');
      var aov = panel.querySelector('[data-pmx-roi-aov]');
      var cr = panel.querySelector('[data-pmx-roi-cr]');
      var lift = panel.querySelector('[data-pmx-roi-lift]');
      if (!traffic || !aov || !cr || !lift) return;

      var invest = parseFloat(panel.getAttribute('data-pmx-invest') || '6000');

      function update() {
        var t = parseFloat(traffic.value);
        var a = parseFloat(aov.value);
        var c = parseFloat(cr.value) / 100;
        var l = parseFloat(lift.value) / 100;

        var newCr = c + l;
        var monthlyNow = t * c * a;
        var monthlyAfter = t * newCr * a;
        var monthlyGain = monthlyAfter - monthlyNow;
        var yearGain = monthlyGain * 12;
        var roi = invest > 0 ? ((yearGain - invest) / invest) * 100 : 0;
        var payback = monthlyGain > 0 ? invest / monthlyGain : 0;

        setText(panel, 'traffic-val', Math.round(t).toLocaleString('en-US').replace(/,/g, ' '));
        setText(panel, 'aov-val', fmtEur(a));
        setText(panel, 'cr-val', c * 100 < 10 ? (c * 100).toFixed(1) + '%' : Math.round(c * 100) + '%');
        setText(panel, 'lift-val', '+' + (l * 100).toFixed(1) + ' pt');

        setText(panel, 'out-now', fmtEur(monthlyNow));
        setText(panel, 'out-after', fmtEur(monthlyAfter));
        setText(panel, 'out-monthly', fmtEur(monthlyGain));
        setText(panel, 'out-year', fmtEur(yearGain));
        setText(panel, 'out-invest', fmtEur(invest));
        setText(panel, 'out-payback', payback > 0 && payback < 240 ? (payback < 1 ? 'under 1 month' : payback.toFixed(1) + ' months') : 'n/a');
        setText(panel, 'out-roi', (roi >= 0 ? '' : '−') + Math.abs(Math.round(roi)).toLocaleString('en-US').replace(/,/g, ' ') + '%');
      }

      function setText(scope, key, value) {
        var el = scope.querySelector('[data-pmx-roi-out="' + key + '"]');
        if (el) el.textContent = value;
      }

      [traffic, aov, cr, lift].forEach(function (input) {
        input.addEventListener('input', update);
        input.addEventListener('change', update);
      });
      update();
    });
  }

  /* ---------- Sticky CTA bar ---------- */
  function initSticky(root) {
    var bars = (root || document).querySelectorAll('[data-pmx-sticky]:not([data-pmx-bound])');
    bars.forEach(function (bar) {
      bar.setAttribute('data-pmx-bound', '1');
      var threshold = parseInt(bar.getAttribute('data-pmx-sticky-after') || '900', 10);
      var ticking = false;

      function check() {
        ticking = false;
        var y = window.pageYOffset || document.documentElement.scrollTop;
        var nearBottom = (y + window.innerHeight) >= (document.body.scrollHeight - 320);
        if (y > threshold && !nearBottom) bar.classList.add('is-visible');
        else bar.classList.remove('is-visible');
      }
      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(check);
      }, { passive: true });
      check();
    });
  }

  /* ---------- Smooth in-page anchors ---------- */
  function initAnchors(root) {
    var links = (root || document).querySelectorAll('.pmx a[href^="#"]:not([data-pmx-bound])');
    links.forEach(function (a) {
      a.setAttribute('data-pmx-bound', '1');
      a.addEventListener('click', function (e) {
        var id = a.getAttribute('href');
        if (!id || id === '#') return;
        var target = document.querySelector(id);
        if (!target) return;
        e.preventDefault();
        var top = target.getBoundingClientRect().top + window.pageYOffset - 70;
        window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    });
  }

  function boot(root) {
    initReveal(root);
    initAccordions(root);
    initCounters(root);
    initRoi(root);
    initSticky(root);
    initAnchors(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  /* Theme editor re-render support */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:select', function (e) { boot(e.target); });
})();
